'use client';

import Link from 'next/link';
import {
    UserPlus,
    Store,
    ShoppingBag,
    CreditCard,
    ArrowRight,
    CheckCircle2
} from 'lucide-react';

export default function SellerGuidePage() {
    const steps = [
        {
            icon: <UserPlus className="w-8 h-8 text-blue-600" />,
            title: "1. Create an Account",
            description: "Sign up for a standard Yunimall user account if you haven't already. This is your gateway to the platform.",
            action: (
                <Link href="/auth/register" className="text-blue-600 font-medium hover:underline inline-flex items-center gap-1">
                    Sign up now <ArrowRight className="w-4 h-4" />
                </Link>
            )
        },
        {
            icon: <Store className="w-8 h-8 text-blue-600" />,
            title: "2. Apply to be a Vendor",
            description: "Navigate to 'Become a Vendor' in your profile menu. You'll need to verify your student or alumni status to ensure our community stays authentic.",
            action: (
                <Link href="/become-vendor" className="text-blue-600 font-medium hover:underline inline-flex items-center gap-1">
                    Apply here <ArrowRight className="w-4 h-4" />
                </Link>
            )
        },
        {
            icon: <CheckCircle2 className="w-8 h-8 text-blue-600" />,
            title: "3. Set Up Your Store",
            description: "Once approved, customize your store front. Upload a logo, add a catchy description, and set your policies.",
        },
        {
            icon: <ShoppingBag className="w-8 h-8 text-blue-600" />,
            title: "4. Add Products",
            description: "List your items with clear photos and detailed descriptions. Proper categorization helps students find what they need.",
        },
        {
            icon: <CreditCard className="w-8 h-8 text-blue-600" />,
            title: "5. Start Selling",
            description: "Manage orders from your dashboard. We facilitate secure payments so you can focus on growing your business.",
        }
    ];

    return (
        <div className="min-h-screen bg-white">
            {/* Hero Section */}
            <div className="bg-[#051449] text-white py-20 px-6">
                <div className="max-w-4xl mx-auto text-center">
                    <h1 className="text-4xl md:text-5xl font-bold mb-6">
                        Start Your Business on Yunimall
                    </h1>
                    <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
                        Turn your side hustle into a thriving campus business. Follow this simple guide to set up your store today.
                    </p>
                    <Link
                        href="/become-vendor"
                        className="inline-block bg-[#FFCF23] text-[#051449] font-bold px-8 py-4 rounded-xl hover:bg-yellow-400 transition-colors"
                    >
                        Start Selling Now
                    </Link>
                </div>
            </div>

            {/* Steps Section */}
            <div className="max-w-4xl mx-auto px-6 py-20">
                <div className="space-y-12">
                    {steps.map((step, index) => (
                        <div key={index} className="flex flex-col md:flex-row gap-6 md:gap-10 items-start">
                            {/* Step Indicator */}
                            <div className="flex-shrink-0 relative">
                                <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center border border-blue-100">
                                    {step.icon}
                                </div>
                                {index !== steps.length - 1 && (
                                    <div className="absolute top-16 left-1/2 -translate-x-1/2 w-0.5 h-full bg-gray-100 -z-10 hidden md:block" style={{ height: 'calc(100% + 48px)' }} />
                                )}
                            </div>

                            {/* Content */}
                            <div className="flex-1 pt-2">
                                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                                    {step.title}
                                </h3>
                                <p className="text-lg text-gray-600 leading-relaxed mb-4">
                                    {step.description}
                                </p>
                                {step.action && (
                                    <div>
                                        {step.action}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Support CTA */}
            <div className="bg-gray-50 py-16 px-6">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">
                        Need help getting started?
                    </h2>
                    <p className="text-gray-600 mb-8">
                        Our support team is here to guide you through every step of the process.
                    </p>
                    <Link
                        href="/help-and-support"
                        className="text-blue-600 font-semibold hover:underline"
                    >
                        Visit Help Center
                    </Link>
                </div>
            </div>
        </div>
    );
}
